import React, { useEffect, useRef } from "react";
import UserSeatGrid from "./UserSeatGrid";
import UserBookingForm from "./UserBookingForm";
import { getSectionAvailability, formatUpperDisplayDate } from "../utils";

const UserSectionModal = ({
  section,
  allBookings,
  availableSeats,
  blockedSeatIds,
  reservedSeatIds,
  permanentSeatIds,
  recurringSeatIds,
  selectedDate,
  requirement,
  currentUser,
  selectedSeatId,
  onSeatSelect,
  bookingForm,
  onSubmit,
  onClose,
  hasExistingBooking,
}) => {
  const modalBodyRef = useRef(null);
  const bookingSummaryRef = useRef(null);

  const selectedSeat = selectedSeatId
    ? section.seats.find((s) => s.id === selectedSeatId)
    : null;

  const { total, available } = getSectionAvailability(
    section,
    allBookings,
    selectedDate,
    requirement,
    availableSeats,
    blockedSeatIds,
    reservedSeatIds,
    permanentSeatIds,
    recurringSeatIds,
  );

  const seatCount = section.seats?.length || 0;
  const maxColsDesktop = Math.min(4, seatCount);

  const modalMaxWidthClassDesktop =
    {
      0: "lg:max-w-[500px]",
      1: "lg:max-w-[650px]",
      2: "lg:max-w-[950px]",
      3: "lg:max-w-[1050px]",
      4: "lg:max-w-[1250px]",
    }[maxColsDesktop] || "lg:max-w-[1250px]";

  const seatColumns = Number(section.seatColumns) || 6;

  // For screens below lg, we dynamically reduce the width if the layout is vertical (few columns)
  // Approximate mobile seat card width: ~130px + gaps + padding
  const mobileSeatAreaWidth = Math.max(1, seatColumns) * 135 + (seatColumns - 1) * 12 + 48;
  const mobileMaxWidth = Math.max(340, mobileSeatAreaWidth); // ensure at least 340px for booking panel to look decent
  const smMaxWidth = Math.max(500, mobileSeatAreaWidth + 60); // ensure at least 500px for sm screens
  const mdMaxWidth = Math.max(500, mobileSeatAreaWidth + 80); // ensure at least 540px for md screens

  useEffect(() => {
    if (!selectedSeatId || typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 1024px)").matches) return;

    const scrollContainer = modalBodyRef.current;
    const target = bookingSummaryRef.current;

    if (!scrollContainer || !target) return;

    requestAnimationFrame(() => {
      const offsetTop = target.offsetTop - scrollContainer.offsetTop - 16;
      scrollContainer.scrollTo({
        top: Math.max(0, offsetTop),
        behavior: "smooth",
      });
    });
  }, [selectedSeatId]);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-950/20 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 lg:p-6">
        <div
          className={`relative flex w-full flex-col overflow-hidden border border-slate-100/80 bg-white shadow-[0_32px_72px_rgba(15,23,42,0.14)] ${
            /* Desktop: fit viewport without scrolling; Mobile/Tablet: fixed premium height */
            "max-h-[95vh] w-[min(calc(100vw-1rem),100%)] sm:w-[min(calc(100vw-2rem),100%)] lg:w-[min(calc(100vw-3rem),100%)] xl:w-full h-[90vh] xl:h-auto xl:min-h-[580px] xl:max-h-[96vh]"
          } max-w-[var(--mobile-max-w)] sm:max-w-[var(--sm-max-w)] md:max-w-[var(--md-max-w)] ${modalMaxWidthClassDesktop}`}
          style={{ 
            "--mobile-max-w": `${mobileMaxWidth}px`,
            "--sm-max-w": `${smMaxWidth}px`,
            "--md-max-w": `${mdMaxWidth}px`
          }}
        >
          {/* ── Header ── */}
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-3 sm:px-6 sm:py-3 lg:px-6 lg:py-3 shrink-0">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-[24px] lg:text-[30px] font-normal tracking-[-0.03em] text-slate-900 leading-tight">
                {section.name}
              </h2>
              <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:gap-x-4 sm:gap-y-2">
                <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1 text-[10px] sm:text-[11px] font-bold tracking-[0.10em] text-slate-400">
                  <span>{formatUpperDisplayDate(selectedDate)}</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
              aria-label="Close seat selection"
            >
              <svg
                className="h-4 w-4 sm:h-5 sm:w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12" />
                <path d="M18 6L6 18" />
              </svg>
            </button>
          </div>

          {/* ── Body – responsive split layout ── */}
          <div
            ref={modalBodyRef}
            className="flex flex-col lg:flex-row min-h-0 flex-1 overflow-auto"
          >
            {/* Left – Seat selection */}
            <div className="w-full min-w-0 lg:flex-1 px-5 py-4 sm:px-6 sm:py-5 lg:px-6 lg:py-6">
              <UserSeatGrid
                section={section}
                allBookings={allBookings}
                availableSeats={availableSeats}
                blockedSeatIds={blockedSeatIds}
                reservedSeatIds={reservedSeatIds}
                permanentSeatIds={permanentSeatIds}
                recurringSeatIds={recurringSeatIds}
                selectedDate={selectedDate}
                currentUser={currentUser}
                requirement={requirement}
                selectedSeatId={selectedSeatId}
                onSeatSelect={onSeatSelect}
                total={total}
                available={available}
              />
            </div>

            {/* Right – Booking panel (always visible, showing "no seat selected" state by default) */}
            <div className="w-full min-w-0 shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-50/10 lg:w-[280px] xl:w-[320px] 2xl:w-[350px]">
              <div className="px-4 py-4 sm:px-6 sm:py-5 lg:px-4 lg:py-6">
                <UserBookingForm
                  summaryRef={bookingSummaryRef}
                  section={section}
                  selectedSeat={selectedSeat}
                  bookingForm={bookingForm}
                  onSubmit={onSubmit}
                  hasExistingBooking={hasExistingBooking}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserSectionModal;
